import { Button, Card, H1, H2, H3, Icon, Input, LineButton, Link, P, Spinner, styled, T, th, useLinks, v, Notification, NotificationText } from '@veracity/vui';
import * as yup from "yup";
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react'

import { Page } from '../components/Page';
import { showErrorDialogAsync } from '../showErrorDialogAsync';
import { Ids } from '../components/Ids';
import { Label } from '../components/Label';
import { InputHelpText } from '../components/InputHelpText';
import { VeracityByDNV } from '../components/VeracityByDNV';
import { DNV } from '../components/DNV';
import { PaperAirplane } from '../components/PaperAirplane';
import { fetchVeracityVoid, ServerError } from '../services/fetchVeracity';
import { getCurrentReturnUrl } from '../utils/getCurrentReturnUrl';
import { identityClient } from '..';
import { PasswordValidationResult } from '../IdentityClient';
import { CancellationToken } from '../utils/CancellationToken';
import { useSearchParams } from 'react-router-dom';
import { ForgotPasswordService } from '../services/auto-generated/ForgotPasswordService';
import { ValidatedForgotPasswordRequestDto } from '../models/auto-generated/ValidatedForgotPasswordRequestDto';
import { delayAsync } from '../utils/delayAsync';
import { PasswordInput } from '../components/PasswordInput';
import { PasswordStrengthMeter } from '../components/PasswordStrengthMeter';
import { ValidationService } from '../services/auto-generated/ValidationService';
import { getUrlWithLoggedInParameterSet } from '../utils/getUrlWithLoggedInParameterSet';
import { PasswordEntropyDto } from '../models/auto-generated/PasswordEntropyDto';

const SLIDE_WIDTH = 387;

export function ResetPasswordPage() {

	let [state, setState] = useState<{ step: ResetPasswordStep; token?: string; validateForgotPasswordRequest?: ValidatedForgotPasswordRequestDto; returnUrl?: string; error?; }>({ step: ResetPasswordStep.FetchingValidatedForgotPasswordRequest });

	if (state.step === ResetPasswordStep.Error)
		throw state.error;

	let [searchParams] = useSearchParams();
	let [links] = useLinks();

	useEffect(() => {

		let token = searchParams.get("token");
		let ct = new CancellationToken();

		(async () => {

			try {

				var validateForgotPasswordRequest = await ForgotPasswordService.validateRequest(token);

				ct.throwIfCancellationRequested();

				if (IS_DEVELOPMENT)
					console.debug("validateForgotPasswordRequest", validateForgotPasswordRequest);
			}

			catch (e) {

				if (ct.isCancellationRequested)
					return;

				if (IS_DEVELOPMENT)
					console.debug(e);

				setState({
					step: ResetPasswordStep.Error,
					error: e
				});

				return;
			}

			setState({
				step: ResetPasswordStep.FetchedValidatedForgotPasswordRequest,
				token,
				validateForgotPasswordRequest
			});

		})();

		return ct.cancel.bind(ct);

	}, []);

	if (state.step === ResetPasswordStep.FetchingValidatedForgotPasswordRequest)
		return <Page key="FETCHING_VALIDATE_FORGOT_PASSWORD_REQUEST" center bg="skyBlue.97"><Spinner text="Please wait..." /></Page>;

	return (
		<Page p={1} bg="skyBlue.97" center>
			<Card column w={SLIDE_WIDTH} overflow="hidden" border="1px solid grey.40" userSelect="none">
				<Slides style={{ left: state.step === ResetPasswordStep.YourPasswordHasBeenChanged ? -SLIDE_WIDTH + "px" : "0" }}>
					<v.div w={SLIDE_WIDTH} p="32px">
						<H3 as="h1">Reset password</H3>
						<Label mt={4}>Email address</Label>
						<T>{state.validateForgotPasswordRequest.Email}</T>
						<Formik
							initialValues={{ emailAddress: state.validateForgotPasswordRequest.Email, newPassword: "", passwordConfirmation: "" }}
							validationSchema={resetPasswordSchema}
							onSubmit={async (values, { setFieldError }) => {

								try {

									await ForgotPasswordService.resetPassword(state.validateForgotPasswordRequest.UserId, state.token, values.newPassword);
								}

								catch (e) {

									if (e instanceof ServerError && e.errorData.subCode === 1010)
										setFieldError("newPassword", "Please enter a more complex password");

									else showErrorDialogAsync("Error when resetting password", e);

									return;
								}

								// Determine the return url

								var returnUrl = getCurrentReturnUrl();

								if (returnUrl) {

									try {

										returnUrl = (await ValidationService.sanitize({ Url: returnUrl })).Url; // Don't trust what's in the browser url
									}

									catch (e) {
										console.error(e);
										returnUrl = links.services;
									}
								}

								if (!returnUrl)
									returnUrl = links.services;

								returnUrl = getUrlWithLoggedInParameterSet(returnUrl);

								if (IS_DEVELOPMENT)
									console.debug("returnUrl", returnUrl);

								setState({ step: ResetPasswordStep.YourPasswordHasBeenChanged, validateForgotPasswordRequest: state.validateForgotPasswordRequest, returnUrl });
							}}
						>
							{({ values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue }) => {

								return (
									<form noValidate onSubmit={handleSubmit}>
										<v.fieldset p={0} disabled={isSubmitting || state.step === ResetPasswordStep.YourPasswordHasBeenChanged}>
											<Ids length={2} fn={ids => (
												<>
													<Label mt={2} htmlFor={ids[0]}>New password</Label>
													<PasswordInput id={ids[0]} mt={1} state={touched.newPassword ? (errors.newPassword ? "error" : "success") : null} variant={touched.newPassword ? (errors.newPassword ? "red" : "green") : null} inputProps={{ autoComplete: "new-password", "aria-invalid": !!(touched.newPassword && errors.newPassword), "aria-errormessage": touched.newPassword && errors.newPassword ? ids[1] : "" }} {...getFieldProps("newPassword")} />
													<PasswordStrengthMeter mt={.5} password={values.newPassword} />
													{touched.newPassword && errors.newPassword && <InputHelpText id={ids[1]} color="red.80">{errors.newPassword}</InputHelpText>}
												</>
											)} />
											<Ids length={2} fn={ids => (
												<>
													<Label mt={2} htmlFor={ids[0]}>Confirm new password</Label>
													<PasswordInput mt={1} id={ids[0]} state={touched.passwordConfirmation ? (errors.passwordConfirmation ? "error" : "success") : null} variant={touched.passwordConfirmation ? (errors.passwordConfirmation ? "red" : "green") : null} inputProps={{ autoComplete: "confirm-password", "aria-invalid": !!(touched.passwordConfirmation && errors.passwordConfirmation), "aria-errormessage": touched.passwordConfirmation && errors.passwordConfirmation ? ids[1] : "" }} {...getFieldProps("passwordConfirmation")} />
													{touched.passwordConfirmation && errors.passwordConfirmation && <InputHelpText id={ids[1]} color="red.80">{errors.passwordConfirmation}</InputHelpText>}


												</>
											)} />
											<Notification mt={2} variant="subtleBlue">
												<NotificationText mx={1.5} my={1.5}>
												Please allow for up to five minutes for changes to take effect
												</NotificationText>
											</Notification>
											<Button mt={3} type="submit" iconRight="falArrowRight" isLoading={isSubmitting}>Continue</Button>
										</v.fieldset>
									</form>
								);
							}}
						</Formik>
					</v.div>
					<v.div w={SLIDE_WIDTH} p={4} display="flex" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center">
						<Icon name="falCheckCircle" color="green.80" scale={3} />
						<H3 as="h1" mt={2}>Your password has been changed</H3>
						<Notification mt={2} variant="subtleBlue">
												<NotificationText mx={1.5} my={1.5}>
												Please allow for up to five minutes for changes to take effect
												</NotificationText>
											</Notification>
						<Button mt={2} as="a" href={state.returnUrl} iconRight="falArrowRight">Continue to sign in</Button>
					</v.div>
				</Slides>
				<v.footer bg="hsl(223, 67%, 18%)" p="24px 32px 32px" color="white">
					<v.div display="flex" justifyContent="space-between">
						<VeracityByDNV />
						<DNV />
					</v.div>
					<P mt={2} fontSize="12px">Veracity is DNV's independent data platform and industry eco-system.</P>
					<P fontSize="12px">Visit <Link fontSize="12px" href={links.home} color="blue.40">www.dnv.com</Link> to discover more.</P>
				</v.footer>
			</Card>
		</Page>
	);
}

const Slides = styled.divBox`
	display: flex;
	position: relative;
	transition: left ${th.animation("standard")} .5s;
	width: ${SLIDE_WIDTH * 2}px;
`;

export const resetPasswordSchema = yup.object().shape({
	emailAddress: yup.string(),
	newPassword: yup.string().test("password", async (value, context) => {

		let validatePassword: { result: PasswordValidationResult; entropy?: PasswordEntropyDto; } = await identityClient.validatePasswordAsync(value || "", null, null, context.parent.emailAddress);

		switch (validatePassword.result) {
			case PasswordValidationResult.Empty:
				return context.createError({ message: "Please enter a password" as any });
			//case PasswordValidationResult.ContainsVeracity:
			//	return context.createError({ message: "Please enter a password that does not contain 'Veracity'" });
			//case PasswordValidationResult.ContainsName:
			//	return context.createError({ message: "Please enter a password that does not contain your name" });
			//case PasswordValidationResult.ContainsEmailAddress:
			//	return context.createError({ message: "Please enter a password that does not contain your e-mail address" });
			//case PasswordValidationResult.ContainsPartOfEmailAddress:
			//	return context.createError({ message: "Please enter a password that does not contain part of your e-mail address" });
			//case PasswordValidationResult.RegExpFailed:
			//	return context.createError({ message: "Please enter a password that is at least 8 characters long, contains both small and capital letters and at least one digit" });
			//case PasswordValidationResult.ScoreTooLow:
			//	let message = "Please enter a more complex password.";
			//	if (validatePassword.entropy?.Feedback?.Warning)
			//		message += " " + validatePassword.entropy.Feedback.Warning;
			//	if (validatePassword.entropy?.Feedback?.Suggestions?.length)
			//		message += " " + validatePassword.entropy.Feedback.Suggestions.join(" ");
			//	return context.createError({ message });
			case PasswordValidationResult.Valid:
				return true;
		}

		//return context.createError({ message: "Please enter a more complex password" });
		return context.createError({ message: <>Password complexity not met. Please see our <Link href="https://help-center.veracity.com/en/articles/6883976-what-is-veracity-s-password-policy" iconRight="falExternalLink" isExternal>password policy</Link> for more information.</> as any });
	}),
	// newPassword: yup.string().required("Please enter a password").test("password", "Please enter a more complex password", (value, context) => {
	// 	return (async () => {
	// 		return (await identityClient.validatePasswordAsync(value || "")).result === PasswordValidationResult.Valid;
	// 	})();
	// }),
	passwordConfirmation: yup.string().required("Please enter a password").oneOf([yup.ref("newPassword")], "Passwords do not match")
});

enum ResetPasswordStep {
	FetchingValidatedForgotPasswordRequest,
	Error,
	FetchedValidatedForgotPasswordRequest,
	YourPasswordHasBeenChanged
}